import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import DataTable, { type DataTableColumn } from '@/components/ui/data-table'
import { PageHeader } from '@/components/ui/page-header'
import { SearchInput } from '@/components/ui/search-input'
import { FilterTabs } from '@/components/ui/filter-tabs'
import { StatusPill } from '@/components/ui/status'
import { money, formatDateTime } from '@/lib/format'
import { get } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { OrderMoneyCell } from '@/components/orders/order-money'
import { DateRangeFilter, applyDateFilter, useDateFilterParams } from '@/components/orders/DateRangeFilter'
import { rangeSummary } from '@shoptrack/common/date-range'
import { UnitFilter, applyUnitChoice, useUnitChoice } from '@/components/network/UnitFilter'

const TAB_STATUS: Record<string, string | null> = {
  all: null,
  urgent: 'failed_shipment',
  pending: 'cho-lay',
  delivering: 'dang-giao',
  success: 'giao-ok',
  returned: 'hoan',
  cancelled: 'huy',
}

export default function CtvOrders() {
  const auth = getAuthState()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // ?filter=urgent (thẻ "đơn cần cứu") · ?status=<trạng thái> (sơ đồ hành trình ở Tổng quan)
  const [activeTab, setActiveTab] = useState(() => {
    if (params.get('filter') === 'urgent') return 'urgent'
    const st = params.get('status')
    return Object.entries(TAB_STATUS).find(([, v]) => v && v === st)?.[0] ?? 'all'
  })
  const [searchTerm, setSearchTerm] = useState('')
  // Lọc theo ngày tạo đơn — giữ trên URL. Số đếm ở các tab tính trên tối đa 100 đơn mới nhất API trả về
  const [dateFilter, setDateFilter] = useDateFilterParams()
  // Lọc theo người bán (?unit=): mặc định tất cả = đơn tự bán + đơn của mọi Shop dưới mình
  const [unit, setUnit] = useUnitChoice(auth.activeShopId)

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['orders', 'ctv', auth.activeShopId, auth.userId, searchTerm, dateFilter.from, dateFilter.to, unit],
    queryFn: () => {
      const p = applyDateFilter(new URLSearchParams(), dateFilter)
      p.set('limit', '100')
      applyUnitChoice(p, unit, auth.activeShopId)
      if (searchTerm) p.set('q', searchTerm)
      return get<{ orders: any[] }>(`/orders?${p.toString()}`)
    },
  })

  const orders = ordersData?.orders ?? []
  const countOf = (status: string) => orders.filter((o) => o.status === status).length
  const wanted = TAB_STATUS[activeTab]
  const filtered = wanted ? orders.filter((o) => o.status === wanted) : orders

  // Đơn của shop trong nhánh mở ở chế độ chỉ xem (?shopId=…) — CTV không sửa đơn của shop.
  const openOrder = (o: any) =>
    navigate(o.shopId && o.shopId !== auth.activeShopId ? `/orders/${encodeURIComponent(o.id)}?shopId=${encodeURIComponent(o.shopId)}` : `/orders/${encodeURIComponent(o.id)}`)

  const columns: DataTableColumn<any>[] = [
    {
      key: 'id',
      header: 'Mã đơn',
      priority: 'primary',
      cell: (o) => (
        <span className="flex flex-wrap items-center gap-x-2">
          <span className="font-mono text-sm">{o.id}</span>
          {o.shopName && <span className="text-xs text-muted">{o.isOwn || o.shopId === auth.activeShopId ? 'Của tôi' : o.shopName}</span>}
        </span>
      ),
    },
    {
      key: 'customer',
      header: 'Khách hàng',
      priority: 'secondary',
      cell: (o) => (
        <span>
          {o.customer?.name || 'Khách lẻ'}
          <span className="font-mono text-muted"> · {o.customer?.phone || '—'}</span>
        </span>
      ),
    },
    {
      key: 'cod',
      header: 'COD',
      align: 'right',
      cell: (o) => <span className="font-mono tabular-nums">{money(o.cod)}</span>,
    },
    {
      key: 'money',
      header: 'Tiền của bạn',
      mobileLabel: 'Của bạn',
      align: 'right',
      // Đơn tự bán: thực nhận. Đơn của Shop: chỉ dòng tiền của chính CTV (chênh lệch + hoa hồng), không phải thực nhận của shop.
      cell: (o) => <OrderMoneyCell money={o.money} seller={!o.shopId || o.shopId === auth.activeShopId} showRole />,
    },
    { key: 'status', header: 'Trạng thái', cell: (o) => <StatusPill status={o.status} /> },
    {
      key: 'shipment',
      header: 'Vận đơn',
      cell: (o) => <span className="font-mono text-xs">{o.shipmentCode || '—'}</span>,
    },
    {
      key: 'createdAt',
      header: 'Tạo lúc',
      align: 'right',
      cell: (o) => <span className="font-mono text-xs tabular-nums text-muted">{formatDateTime(o.createdAt)}</span>,
    },
    {
      key: 'action',
      header: '',
      priority: 'action',
      // Chỉ đơn gặp sự cố mới có hành động: gọi khách để cứu đơn. Đơn của shop là của shop —
      // CTV không sửa / giao lại thay (máy chủ chỉ cho chủ đơn sửa).
      cell: (o) =>
        o.status === 'failed_shipment' && o.customer?.phone ? (
          <span className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Button asChild size="sm" variant="outline">
              <a href={`tel:${o.customer.phone}`}>Gọi khách</a>
            </Button>
          </span>
        ) : null,
    },
  ]

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <PageHeader title="Đơn hàng" />

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-full sm:w-80">
            <SearchInput
              aria-label="Tìm tên khách, số điện thoại, mã vận đơn"
              placeholder="Tìm tên khách, số điện thoại, mã vận đơn…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <DateRangeFilter value={dateFilter} onChange={setDateFilter} />
          <UnitFilter value={unit} onChange={setUnit} />
        </div>
        <FilterTabs
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { value: 'all', label: 'Tất cả', count: orders.length },
            { value: 'urgent', label: 'Cần cứu', count: countOf('failed_shipment') },
            { value: 'pending', label: 'Chờ lấy', count: countOf('cho-lay') },
            { value: 'delivering', label: 'Đang giao', count: countOf('dang-giao') },
            { value: 'success', label: 'Đã giao', count: countOf('giao-ok') },
            { value: 'returned', label: 'Đã hoàn', count: countOf('hoan') },
            { value: 'cancelled', label: 'Đã hủy', count: countOf('huy') },
          ]}
        />
      </div>

      <div className="overflow-hidden rounded-xl bg-raise shadow-card">
        <DataTable
          columns={columns}
          data={filtered}
          rowKey={(o) => `${o.shopId ?? ''}_${o.id}`}
          loading={isLoading}
          onRowClick={openOrder}
          emptyLabel={dateFilter.key !== 'all' ? 'Không có đơn' : 'Chưa có đơn'}
        />
        {filtered.length > 0 && (
          <p className="border-t border-rule px-4 py-2.5 text-xs text-muted">
            {filtered.length} đơn{orders.length >= 100 ? ' (trong 100 đơn mới nhất)' : ''}
            {dateFilter.key !== 'all' && (dateFilter.from || dateFilter.to) && ` · tạo ${rangeSummary(dateFilter.from, dateFilter.to)}`}
          </p>
        )}
      </div>
    </div>
  )
}
